import {
	filterBookingTimetablesByUniqueStartAndEndTimes,
	normalizeBookingTimetablesSingleResource,
} from 'helpers/bookingTemplates';
import { getIdFromObject, uniq } from 'helpers/common';
import { IBookingTimeTable, IResourceTimeTables } from 'models/shop/bookingtimetables';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import moment = require('moment');
import { IStore } from 'redux/IStore';
import { getOfficeBookingTemplates } from '../helpers';

export function filterResourceOfficeTimetables(
	bookingTemplates: IServicesList[],
	timetables: IBookingTimeTable[],
	officeId: string,
	resourceId: IBookingTimeTable['resourceId'],
): IBookingTimeTable[] {
	const officeBookingTemplateIds = bookingTemplates.filter((btt) => btt.officeId === officeId).map((btt) => btt.id);
	const allShopResourceTimetables = timetables.filter((tt) => tt.resourceId === resourceId && tt.officeId === officeId);
	return allShopResourceTimetables.filter((tt) => officeBookingTemplateIds.includes(tt.bookingTemplateId));
}

export function getResourceTimetables(
	getState: () => IStore,
	resourceId: IBookingTimeTable['resourceId'],
	dateLimit?: [moment.Moment, moment.Moment],
): Promise<IBookingTimeTable[]> {
	if (!resourceId) {
		return Promise.resolve([]);
	}
	if (!dateLimit) {
		dateLimit = [moment().add(-30, 'days'), moment().add(60, 'days')];
	}
	return fetch(
		getState().fetchDefault.baseAPI +
			'api/shop/ResourceTimeTables' +
			'?resourceId=' +
			resourceId +
			'&startDateFrom=' +
			dateLimit[0].toISOString() +
			'&startDateTo=' +
			dateLimit[1].toISOString(),
		{
			...getState().fetchDefault.requestInit,
		},
	).then((res) => {
		if (res.ok) {
			return res.json();
		} else {
			throw res;
		}
	});
}

export function batchAddBookingTimetablesToServices(
	getState: () => IStore,
	services: Array<Pick<IServicesList, 'id' | 'officeId' | 'resources'>>,
	officeId: string,
): Promise<any> {
	if (!services.length) {
		return Promise.resolve(0);
	}
	const variousResourcesAndOffices: Array<{ resourceId: string; officeId: string; serviceId: string }> = [];
	for (const s of services) {
		const resourcesIds = s.resources.map(getIdFromObject);
		for (const resourceId of resourcesIds) {
			if (
				!variousResourcesAndOffices.some(
					(vro) => vro.resourceId === resourceId && vro.officeId === s.officeId && vro.serviceId === s.id,
				)
			) {
				variousResourcesAndOffices.push({ resourceId, officeId: s.officeId, serviceId: s.id });
			}
		}
	}
	if (!variousResourcesAndOffices.length) {
		return Promise.resolve(0);
	}
	const variousResourcesIds = uniq(variousResourcesAndOffices.map((t) => t.resourceId)).filter(Boolean);
	return Promise.all([
		getOfficeBookingTemplates(getState, officeId),
		Promise.all(
			variousResourcesIds.map((resourceId) =>
				getResourceTimetables(getState, resourceId).then((timeTables) => ({
					resourceId,
					timeTables,
				})),
			),
		),
	]).then(([allTemplates, allTimeTablesResourcesTuples]) =>
		Promise.all(
			variousResourcesAndOffices.map((resourceAndOfficeTuple) => {
				const { resourceId, officeId, serviceId } = resourceAndOfficeTuple;
				// Timetables for this resource
				const timetablesTuple = allTimeTablesResourcesTuples.find(
					(t) => t.resourceId === resourceAndOfficeTuple.resourceId,
				);
				const combined = filterResourceOfficeTimetables(allTemplates, timetablesTuple.timeTables, officeId, resourceId);
				const timezone = getState().shopcard?.shopcard?.offices?.find((o) => o.id === officeId)?.zoneInfo;
				const timetablesGroups = normalizeBookingTimetablesSingleResource(combined, timezone);
				const bookingTimeTables = timetablesGroups.map((ttGroup) => {
					for (const item of ttGroup.bookingTimetables) {
						if (item.resourceId === resourceId && item.bookingTemplateId === serviceId && item.officeId === officeId) {
							return;
						}
					}
					return {
						id: undefined,
						officeId: officeId,
						bookingTemplateId: serviceId,
						resourceId: resourceId,
						startTime: ttGroup.startTime,
						endTime: ttGroup.endTime,
					} as IBookingTimeTable;
				});

				return postBookingTimetable(getState, bookingTimeTables);
			}),
		),
	);
}

export function updatedBatchAddBookingTimeTablesToServices(
	getState: () => IStore,
	allServices: IServicesList[],
	services: Array<Pick<IServicesList, 'id' | 'officeId' | 'resources'>>,
	allTimeTablesResourcesTuples: IResourceTimeTables[],
): Promise<any> {
	if (!services.length || !allServices.length) {
		return Promise.resolve(0);
	}

	const variousResourcesAndOffices: Array<{ resourceId: string; officeId: string; serviceId: string }> = [];

	for (const s of services) {
		const resourcesIds = s.resources.map(getIdFromObject);

		for (const resourceId of resourcesIds) {
			if (
				!variousResourcesAndOffices.some(
					(vro) => vro.resourceId === resourceId && vro.officeId === s.officeId && vro.serviceId === s.id,
				)
			) {
				variousResourcesAndOffices.push({ resourceId, officeId: s.officeId, serviceId: s.id });
			}
		}
	}

	if (!variousResourcesAndOffices.length) {
		return Promise.resolve(0);
	}

	const allBookingTimeTables: IBookingTimeTable[] = [];

	variousResourcesAndOffices.map((resourceAndOfficeTuple) => {
		const { resourceId, officeId, serviceId } = resourceAndOfficeTuple;
		// Timetables for this resource
		const timetablesTuple = allTimeTablesResourcesTuples.find(
			(t) => t.resourceId === resourceAndOfficeTuple.resourceId,
		);
		const combined = filterResourceOfficeTimetables(allServices, timetablesTuple.timeTables, officeId, resourceId);
		const timezone = getState().shopcard?.shopcard?.offices?.find((o) => o.id === officeId)?.zoneInfo;

		// Filter the combined resource time tables of the resource by start and end times
		const filteredTimetablesUnique = filterBookingTimetablesByUniqueStartAndEndTimes(combined, timezone);

		// This normalization is really heavy because we only need the unique start and end times of the existing time tables and not the entire structure
		// const timetablesGroups = normalizeBookingTimetablesSingleResource(combined);

		const bookingTimeTables = filteredTimetablesUnique.map((ttGroup) => {
			return {
				id: undefined,
				officeId: officeId,
				bookingTemplateId: serviceId,
				resourceId: resourceId,
				startTime: ttGroup.startTime,
				endTime: ttGroup.endTime,
			} as IBookingTimeTable;
		});

		allBookingTimeTables.push(...bookingTimeTables);
	});

	return postBookingTimetable(getState, allBookingTimeTables);
}

export function postBookingTimetable(getState: () => IStore, bookingTimeTable: IBookingTimeTable[]): Promise<boolean> {
	if (bookingTimeTable.length == 0) {
		return Promise.resolve(true);
	}

	return fetch(getState().fetchDefault.baseAPI + 'api/shop/bookingTimeTables', {
		...getState().fetchDefault.requestInit,
		method: 'POST',
		body: JSON.stringify(bookingTimeTable),
	}).then((res) => {
		if (res.ok) {
			return Promise.resolve(true);
		} else {
			return Promise.reject(res);
		}
	});
}

export function postPartiallyUnavailableBookingTimetable(
	getState: () => IStore,
	bookingTimeTables: IBookingTimeTable[],
): Promise<boolean> {
	if (bookingTimeTables.length == 0) {
		return Promise.resolve(true);
	}

	return fetch(getState().fetchDefault.baseAPI + 'api/shop/PartiallyUnavailableTimeTables', {
		...getState().fetchDefault.requestInit,
		method: 'POST',
		body: JSON.stringify(bookingTimeTables),
	}).then((res) => {
		if (res.ok) {
			return Promise.resolve(true);
		} else {
			return Promise.reject(bookingTimeTables);
		}
	});
}
